import React from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { UserForm } from '../../components/onboarding';
import PerfectScrollbar from 'react-perfect-scrollbar';

import EditableUserInfos from '@/types/editable-user-infos';
import User from '@/types/user';
import withLegacyVersion from '@/views/components/hocs/withLegacyVersion';
import LegacySettingsContact from './LegacySettingsContact';
import { formatMessage, FormattedMessage } from '@/utils/i18n';
import { ImageUploader } from '@/views/components/image-upload';
import ColorInput from '@/views/components/misc/ColorInput';
import { PERMISSIONS, userHasPermission } from '@/utils/permissions';
import notification from '../../../utils/notification';

type Props = {
  user: User;
  onSave: (info: EditableUserInfos) => Promise<User>;
} & RouteComponentProps;

const SettingsContact = ({ user, onSave }: Props) => {
  const onSaveInfo = async (info: EditableUserInfos) => {
    await onSave(info);
    notification.success(formatMessage('Onboarding.Success'));
  };

  const onConfirmDeletePhoto = async () => {
    await onSave({ imgUrl: null });
    notification.success(formatMessage('Onboarding.ProfilePicture.DeleteModal.DeleteSuccess'));
  };

  const onConfirmDeleteLogo = async () => {
    await onSave({ 'coBranding.logoUrl': null } as EditableUserInfos);
    notification.success(formatMessage('Onboarding.CompanyLogo.DeleteModal.DeleteSuccess'));
  };

  const onSaveBrandColor = async (color: string) => {
    try {
      await onSave({ coBranding: { colors: { primary: color } } });
    } catch (error) {
      notification.error(formatMessage('Onboarding.BrandColor.SaveError'));
      throw error;
    }
  };

  return (
    <div className="settings-contact-v2 settings-tab">
      <PerfectScrollbar className="settings-contact-content-psb">
        <div className="user-onboarding-v2">
          <FormattedMessage tagName="h1" id="Onboarding.Title" />

          <div className="grid-section branding">
            <ImageUploader imgUrl={user.imgUrl} i18nPath="Onboarding.ProfilePicture" onDelete={onConfirmDeletePhoto} />

            {userHasPermission(user, PERMISSIONS.PROPOSAL_COBRANDING) && (
              <>
                <ImageUploader imgUrl={user.coBranding?.logoUrl} i18nPath="Onboarding.CompanyLogo" onDelete={onConfirmDeleteLogo} isUploadCoBrandingLogo />

                <div className="brand-color">
                  <h3 id="brand-color-title" className="label-v2">
                    <FormattedMessage id="Onboarding.BrandColor.Title" />
                  </h3>
                  <label id="brand-color-description">
                    <FormattedMessage id="Onboarding.BrandColor.Description" />
                  </label>
                  <ColorInput
                    dropdown
                    onChange={onSaveBrandColor}
                    selected={user.coBranding?.colors?.primary}
                    aria-labelledby="brand-color-title"
                    aria-describedby="brand-color-description"
                  />
                </div>
              </>
            )}
          </div>

          <UserForm user={user} onSubmit={onSaveInfo} />
        </div>
      </PerfectScrollbar>
    </div>
  );
};

export default withLegacyVersion(SettingsContact, LegacySettingsContact);
